// Single renderer for the creator preview, the cart previews and the print
// file, so what the customer sees is exactly what gets printed.
import { getGeometry } from "./geometry";
import { normalizeFamily } from "./fonts";

const PREVIEW_OUTLINE = "#cfd4da";
const CUT_LINE = "#E6007E";
const ROW_GAP = 0.12;
const FALLBACK_METRICS = { ascent: 0.92, descent: 0.26 };

let measureCanvas = null;

export function escapeXml(value) {
    return String(value)
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&apos;");
}

function n(value) {
    return Math.round(value * 100) / 100;
}

function clamp(value, min, max) {
    return Math.min(max, Math.max(min, value));
}

function fontMetrics(font) {
    if (!font) {
        return FALLBACK_METRICS;
    }
    return {
        ascent: font.ascender / font.unitsPerEm,
        descent: -font.descender / font.unitsPerEm
    };
}

// Width of the text at font size 1.
function measureText(text, font, family) {
    if (font) {
        return font.getAdvanceWidth(text, 1, { kerning: true });
    }
    if (typeof document === "undefined") {
        return text.length * 0.55;
    }
    measureCanvas = measureCanvas || document.createElement("canvas");
    const ctx = measureCanvas.getContext("2d");
    ctx.font = `100px "${family}", sans-serif`;
    return ctx.measureText(text).width / 100;
}

export function stickerRows(sticker) {
    return clamp(parseInt(sticker && sticker.rows, 10) || 1, 1, 3);
}

export function hasFigure(sticker, design) {
    return parseInt(sticker.figure, 10) === 1 && design.allowedFigure !== false;
}

export function hasBackgroundImage(sticker, design) {
    return (
        parseInt(sticker.background, 10) === 1 &&
        design.allowedBackground !== false
    );
}

/**
 * Positions of the figure and every text row, in sticker units.
 *
 * sticker: admin record (rows, img_height, base_font_size, view_box, d...)
 * design:  creator model / cart product_data
 * options: { font, fontFamily, figureAspect, hasFigureImage }
 */
export function computeLayout(sticker, design, options = {}) {
    const geometry = getGeometry(sticker);
    const { viewBox, content } = geometry;
    const metrics = fontMetrics(options.font);
    const family = normalizeFamily(design.selectedFont) || "sans-serif";

    let figure = null;
    let textBox = { ...content };

    if (hasFigure(sticker, design) && options.hasFigureImage) {
        const factor = clamp(parseFloat(sticker.img_height) || 0.8, 0.2, 1);
        const height = Math.min(viewBox.height * factor, viewBox.height);
        const aspect = clamp(options.figureAspect || 1, 0.3, 3);
        const width = Math.min(height * aspect, content.width * 0.45);
        const margin = geometry.isRound
            ? content.x - viewBox.x
            : (viewBox.height - height) / 2;
        const y = viewBox.y + (viewBox.height - height) / 2;
        const onRight = parseInt(design.selectedSide, 10) === 1;
        const x = onRight
            ? viewBox.x + viewBox.width - margin - width
            : viewBox.x + margin;
        figure = { x, y, width, height };

        const gap = content.height * 0.08;
        textBox = onRight
            ? {
                  x: content.x,
                  y: content.y,
                  width: Math.max(x - gap - content.x, content.width * 0.3),
                  height: content.height
              }
            : {
                  x: x + width + gap,
                  y: content.y,
                  width: Math.max(
                      content.x + content.width - (x + width + gap),
                      content.width * 0.3
                  ),
                  height: content.height
              };
    }

    const rows = stickerRows(sticker);
    const texts = (Array.isArray(design.text) ? design.text : [])
        .slice(0, rows)
        .map(t => (t == null ? "" : String(t).trim()))
        .filter(t => t.length > 0);

    const lines = [];
    if (texts.length > 0) {
        const lineFactor = metrics.ascent + metrics.descent;
        const baseDivisor = parseFloat(sticker.base_font_size);
        const maxSize =
            baseDivisor > 0
                ? (viewBox.height / baseDivisor) * 0.9
                : textBox.height / rows / lineFactor;
        const slot =
            textBox.height /
            (texts.length + ROW_GAP * (texts.length - 1)) /
            lineFactor;

        const sized = texts.map(text => {
            const width = measureText(text, options.font, family);
            const byWidth = width > 0 ? textBox.width / width : maxSize;
            const size = Math.max(0.1, Math.min(maxSize, slot, byWidth));
            return { text, size, width: width * size };
        });

        const gap = Math.min(...sized.map(s => s.size)) * ROW_GAP;
        const total =
            sized.reduce((sum, s) => sum + s.size * lineFactor, 0) +
            gap * (sized.length - 1);
        let top = textBox.y + (textBox.height - total) / 2;

        sized.forEach(s => {
            lines.push({
                text: s.text,
                size: s.size,
                x: textBox.x + (textBox.width - s.width) / 2,
                centerX: textBox.x + textBox.width / 2,
                baseline: top + s.size * metrics.ascent
            });
            top += s.size * lineFactor + gap;
        });
    }

    return { geometry, figure, textBox, lines, family };
}

function backgroundLayer(sticker, design, assets, clipId, geometry) {
    const { viewBox, shape } = geometry;
    const color = design.backgroundColor || "#ffffff";
    let out = `<path d="${escapeXml(shape)}" fill="${escapeXml(color)}"/>`;

    if (hasBackgroundImage(sticker, design) && assets.background) {
        out += assets.background.inline
            ? assets.background.inline(viewBox, "xMidYMid slice")
            : `<image xlink:href="${escapeXml(assets.background.href)}" x="${n(
                  viewBox.x
              )}" y="${n(viewBox.y)}" width="${n(viewBox.width)}" height="${n(
                  viewBox.height
              )}" preserveAspectRatio="xMidYMid slice"/>`;
    }
    return `<g id="Background" clip-path="url(#${clipId})">${out}</g>`;
}

function figureLayer(layout, assets, clipId) {
    if (!layout.figure || !assets.figure) {
        return "";
    }
    const box = layout.figure;
    const body = assets.figure.inline
        ? assets.figure.inline(box, "xMidYMid meet")
        : `<image xlink:href="${escapeXml(assets.figure.href)}" x="${n(
              box.x
          )}" y="${n(box.y)}" width="${n(box.width)}" height="${n(
              box.height
          )}" preserveAspectRatio="xMidYMid meet"/>`;
    return `<g id="Figure" clip-path="url(#${clipId})">${body}</g>`;
}

function textLayer(layout, design, font) {
    if (!layout.lines.length) {
        return "";
    }
    const color = escapeXml(design.textColor || "#222222");
    const body = layout.lines
        .map(line => {
            if (font) {
                const d = font
                    .getPath(line.text, line.x, line.baseline, line.size, {
                        kerning: true
                    })
                    .toPathData(2);
                return `<path d="${d}"/>`;
            }
            return `<text x="${n(line.centerX)}" y="${n(
                line.baseline
            )}" font-size="${n(line.size)}" text-anchor="middle">${escapeXml(
                line.text
            )}</text>`;
        })
        .join("");
    const familyAttr = font
        ? ""
        : ` font-family="${escapeXml(`'${layout.family}', sans-serif`)}"`;
    return `<g id="Text" fill="${color}"${familyAttr}>${body}</g>`;
}

/**
 * Returns the sticker as an SVG document string.
 *
 * options.mode:    "preview" (screen) or "print" (adds the cut line)
 * options.font:    opentype.js font; text is outlined when present
 * options.assets:  { figure, background } each { href } or { inline(box, preserve) }
 * options.figureAspect: width / height of the figure image
 * options.uid:     unique id prefix when several stickers share a page
 */
export function renderSticker(sticker, design, options = {}) {
    const assets = options.assets || {};
    const layout = computeLayout(sticker, design, {
        font: options.font,
        figureAspect: options.figureAspect,
        hasFigureImage: Boolean(assets.figure)
    });
    const { geometry } = layout;
    const { viewBox, shape } = geometry;
    const uid = options.uid || "sticker";
    const clipId = `${uid}-clip`;
    const isPrint = options.mode === "print";

    const outline = isPrint
        ? `<g id="CutContour"><path d="${escapeXml(
              shape
          )}" fill="none" stroke="${CUT_LINE}" stroke-width="${n(
              Math.max(viewBox.height * 0.0025, 0.1)
          )}"/></g>`
        : `<path d="${escapeXml(
              shape
          )}" fill="none" stroke="${PREVIEW_OUTLINE}" stroke-width="1" vector-effect="non-scaling-stroke"/>`;

    const size = isPrint
        ? ` width="${n(viewBox.width)}" height="${n(viewBox.height)}"`
        : "";
    const title = options.title
        ? `<title>${escapeXml(options.title)}</title>`
        : "";

    return (
        `<svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" ` +
        `viewBox="${n(viewBox.x)} ${n(viewBox.y)} ${n(viewBox.width)} ${n(
            viewBox.height
        )}"${size} preserveAspectRatio="xMidYMid meet">` +
        title +
        `<defs><clipPath id="${clipId}"><path d="${escapeXml(
            shape
        )}"/></clipPath></defs>` +
        backgroundLayer(sticker, design, assets, clipId, geometry) +
        figureLayer(layout, assets, clipId) +
        textLayer(layout, design, options.font) +
        outline +
        `</svg>`
    );
}
